<template>
  <component :is="tagName">
    <teleport
      to="body"
      :disabled="!appendToBody"
    >
      <span
        v-show="!disabled && showPopper"
        ref="popper"
        :class="rootClass"
      >
        <slot>{{ content }}</slot>
      </span>
    </teleport>
    <span ref="reference">
      <slot name="reference" />
    </span>
  </component>
</template>

<script>
import Popper from 'popper.js';

// Vue 3 port of `vue-popperjs` (Vue-2-only — its render functions crash under Vue 3).
// Keeps the same public API for the existing consumers (JobSiteAction,
// ClientLocationSettingOptions): `reference`/default slots, `trigger`
// (hover | click | clickToToggle | clickToOpen), `:options="{ placement }"`,
// `append-to-body`, `visible-arrow`, `disabled`. Registered globally as
// `VuePopper`, mirroring the Vue 2 hub_app registration.
export default {
  name: 'VuePopper',
  props: {
    tagName: { type: String, default: 'span' },
    trigger: {
      type: String,
      default: 'hover',
      validator: (value) => ['clickToOpen', 'click', 'clickToToggle', 'hover', 'focus'].indexOf(value) > -1,
    },
    delayOnMouseOver: { type: Number, default: 10 },
    delayOnMouseOut: { type: Number, default: 10 },
    disabled: { type: Boolean, default: false },
    content: { type: String, default: '' },
    reference: { type: Object, default: null },
    forceShow: { type: Boolean, default: false },
    appendToBody: { type: Boolean, default: false },
    visibleArrow: { type: Boolean, default: true },
    stopPropagation: { type: Boolean, default: false },
    preventDefault: { type: Boolean, default: false },
    options: {
      type: Object,
      default() {
        return {};
      },
    },
    rootClass: { type: String, default: '' },
  },
  emits: ['show', 'hide', 'created'],
  data() {
    return {
      showPopper: false,
    };
  },
  watch: {
    showPopper(value) {
      if (value) {
        this.$emit('show', this);
        this.updatePopper();
      } else {
        this.$emit('hide', this);
      }
    },
    forceShow: {
      handler(value) {
        this[value ? 'doShow' : 'doClose']();
      },
      immediate: true,
    },
    disabled(value) {
      if (value) {
        this.showPopper = false;
      }
    },
  },
  mounted() {
    // Non-reactive instance refs, as in the original — Vue must not proxy the Popper instance.
    this.referenceElm = this.reference || this.$refs.reference;
    this.popperElm = this.$refs.popper;
    this.popperJS = null;
    this._timer = null;

    switch (this.trigger) {
      case 'clickToOpen':
        this.referenceElm.addEventListener('click', this.doShow);
        document.addEventListener('click', this.handleDocumentClick);
        break;
      case 'click':
      case 'clickToToggle':
        this.referenceElm.addEventListener('click', this.doToggle);
        document.addEventListener('click', this.handleDocumentClick);
        break;
      case 'hover':
        this.referenceElm.addEventListener('mouseover', this.onMouseOver);
        this.popperElm.addEventListener('mouseover', this.onMouseOver);
        this.referenceElm.addEventListener('mouseout', this.onMouseOut);
        this.popperElm.addEventListener('mouseout', this.onMouseOut);
        break;
      case 'focus':
        // focusin/focusout (not focus/blur): the listeners sit on the wrapper span, and only the
        // bubbling variants fire when the focusable element inside the reference slot gets focus.
        this.referenceElm.addEventListener('focusin', this.onMouseOver);
        this.referenceElm.addEventListener('focusout', this.onMouseOut);
        this.popperElm.addEventListener('focusin', this.onMouseOver);
        this.popperElm.addEventListener('focusout', this.onMouseOut);
        break;
    }
  },
  beforeUnmount() {
    clearTimeout(this._timer);
    this.referenceElm?.removeEventListener('click', this.doShow);
    this.referenceElm?.removeEventListener('click', this.doToggle);
    this.referenceElm?.removeEventListener('mouseover', this.onMouseOver);
    this.referenceElm?.removeEventListener('mouseout', this.onMouseOut);
    this.referenceElm?.removeEventListener('focusin', this.onMouseOver);
    this.referenceElm?.removeEventListener('focusout', this.onMouseOut);
    this.popperElm?.removeEventListener('mouseover', this.onMouseOver);
    this.popperElm?.removeEventListener('mouseout', this.onMouseOut);
    this.popperElm?.removeEventListener('focusin', this.onMouseOver);
    this.popperElm?.removeEventListener('focusout', this.onMouseOut);
    document.removeEventListener('click', this.handleDocumentClick);

    if (this.popperJS) {
      this.popperJS.destroy();
      this.popperJS = null;
    }
  },
  methods: {
    doToggle(event) {
      if (this.stopPropagation) {
        event.stopPropagation();
      }

      if (this.preventDefault) {
        event.preventDefault();
      }

      if (!this.forceShow) {
        this.showPopper = !this.showPopper;
      }
    },
    doShow() {
      this.showPopper = true;
    },
    doClose() {
      this.showPopper = false;
    },
    updatePopper() {
      if (this.popperJS) {
        this.popperJS.scheduleUpdate();
      } else {
        this.createPopper();
      }
    },
    createPopper() {
      this.$nextTick(() => {
        if (this.visibleArrow) {
          this.appendArrow(this.popperElm);
        }

        const popperOptions = {
          placement: 'bottom',
          computeStyle: { gpuAcceleration: false },
          ...this.options,
          onCreate: () => {
            this.$emit('created', this);
            this.$nextTick(() => this.popperJS?.scheduleUpdate());
          },
        };

        this.popperJS = new Popper(this.referenceElm, this.popperElm, popperOptions);
      });
    },
    appendArrow(element) {
      if (this.appendedArrow || !element) {
        return;
      }

      this.appendedArrow = true;

      const arrow = document.createElement('div');

      arrow.setAttribute('x-arrow', '');
      arrow.className = 'popper__arrow';
      element.appendChild(arrow);
    },
    onMouseOver() {
      clearTimeout(this._timer);
      this._timer = setTimeout(() => {
        this.showPopper = true;
      }, this.delayOnMouseOver);
    },
    onMouseOut() {
      clearTimeout(this._timer);
      this._timer = setTimeout(() => {
        this.showPopper = false;
      }, this.delayOnMouseOut);
    },
    handleDocumentClick(event) {
      if (
        !this.$el ||
        !this.referenceElm ||
        !this.popperElm ||
        this.$el.contains(event.target) ||
        this.referenceElm.contains(event.target) ||
        this.popperElm.contains(event.target)
      ) {
        return;
      }

      if (this.forceShow) {
        return;
      }

      this.showPopper = false;
    },
  },
};
</script>
